import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from '../../redux/store';
import useAuth from 'src/hooks/useAuth';
import useTabs from 'src/hooks/useTabs';

// Actions
import actionBuscarCampanhaById from 'src/actions/campanhas/actionBuscarCampanhaById';
import actionCriarCampanha from 'src/actions/campanhas/actionCriarCampanha';
import actionDescontinuarcampanha from 'src/actions/campanhas/actionDescontinuarcampanha';
import actionEditarCampanha from 'src/actions/campanhas/actionEditarCampanha';
import actionListaCampanhas from 'src/actions/campanhas/actionListaCampanhas';
import actionCampanhasEstimativa from 'src/actions/campanhas/actionCampanhasEstimativa';
import actionListaClientesCanais from 'src/actions/clientes/actionListaClientesCanais';
import v2_actionUploadS3 from 'src/actions/v2/global/actionUploadS3';
import v2_actionConfigCrm from 'src/actions/v2/crm/v2_actionConfigCrm';
import v2_actionListarListas from 'src/actions/v2/contatos/v2_actionListarListas';
import { getListaOptionsTags } from 'src/redux/slices/global';

import v3_marketing_criarCampanhas_action from 'src/actions/v3/marketing/campanhas/v3.marketing.criarCampanhas.action';
import v3_marketing_listarJobs_action from 'src/actions/v3/marketing/campanhas/v3.marketing.listarJobs.action';
import v3_marketing_deletarJob_action from 'src/actions/v3/marketing/campanhas/v3.marketing.deletarJob.action';
import v1_actionWboTemplates from "src/actions/v1/meta/wbo/v1_actionWboTemplates";

// Utils
import MicRecorder from "mic-recorder-to-mp3";
import { fDateTimeSuffix } from 'src/utils/formatTime';
import { cn } from 'src/@/lib/utils';

// Icons
import Iconify from 'src/components/Iconify';
import { BsMicFill } from 'react-icons/bs';
import { FaCheck } from 'react-icons/fa';
import { Trash2, Edit2, Play, Pause, AlertCircle, Plus, Search, Filter, X, ChevronRight, ChevronLeft, Save, Eye } from 'lucide-react';

// UI Components
import { PopoverVariaveis } from "src/sections/@dashboard/automacoes/components/PopoverVariaveis";

import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'src/@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from 'src/@/components/ui/tabs';
import { Switch } from 'src/@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from 'src/@/components/ui/radio-group';
import { Label } from 'src/@/components/ui/label';
import { Checkbox } from 'src/@/components/ui/checkbox';
import { Badge } from 'src/@/components/ui/badge';
import { Textarea } from 'src/@/components/ui/textarea';
import { MultiSelect } from 'src/@/components/ui/multi-select';
import { ScrollArea } from 'src/@/components/ui/scroll-area';
import { toast } from 'src/@/components/ui/use-toast';
import { Separator } from 'src/@/components/ui/separator';

// Design
import { CAMPANHAS_DESIGN } from './Campanhas.design';

const STATUS_OPTIONS = [
    { value: 'all', label: "Todos" },
    { value: 'agendado', label: "Agendados" },
    { value: 'em andamento', label: "Em Andamento" },
    { value: 'finalizado', label: "Finalizado" },
    { value: 'erro', label: "Falhados" }
];

const TABLE_HEAD = [
    { id: 'nome', label: 'Nome' },
    { id: 'status_envio', label: 'Status Envio' },
    { id: 'criado_por', label: 'Criado Por' },
    { id: 'criado_em', label: 'Criado em' },
    { id: 'tipo', label: 'Tipo' },
    { id: 'data_envio', label: 'Data Envio' },
    { id: 'status', label: 'Ativo' },
    { id: 'actions', label: '' },
];

const STEPS = [
    { id: 1, label: "Identificação", subtitle: "Defina o nome e tipo" },
    { id: 2, label: "Público Alvo", subtitle: "Quem receberá a mensagem?" },
    { id: 3, label: "Conteúdo", subtitle: "Personalize sua mensagem" },
    { id: 4, label: "Agendamento", subtitle: "Quando enviar?" }
];

export default function Campanhas() {
    const dispatch = useDispatch();
    const { user, cliente } = useAuth();
    const { optionsTags } = useSelector((state) => state.global);

    // State
    const [tableData, setTableData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [filterName, setFilterName] = useState('');
    const [currentTab, setCurrentTab] = useState('all');

    // Form and Dialog State
    const [dialogOpen, setDialogOpen] = useState(false);
    const [modalConfirmarExclusao, setModalConfirmarExclusao] = useState(false);
    const [campanhaId, setCampanhaId] = useState('');
    const [loadingEnviarArquivos, setLoadingEnviarArquivos] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [activeStep, setActiveStep] = useState(1);

    // Jobs Modal State
    const [jobsModalOpen, setJobsModalOpen] = useState(false);
    const [jobsData, setJobsData] = useState({ jobs: [], summary: {} });
    const [loadingJobs, setLoadingJobs] = useState(false);

    // Form Fields
    const [nomeCampanha, setNomeCampanha] = useState('');
    const [tipoCampanha, setTipoCampanha] = useState(''); // 'funil' or 'tag'
    const [funilId, setFunilId] = useState('');
    const [funilIds, setFunilIds] = useState([]); // Estágios do funil
    const [tagsIds, setTagsIds] = useState([]);
    const [contactLists, setContactLists] = useState([]);
    const [selectedContactLists, setSelectedContactLists] = useState([]);
    const [csvFile, setCsvFile] = useState(null);
    const [body, setBody] = useState('');
    const [dataCampanha, setDataCampanha] = useState(new Date().toISOString().split('T')[0]);
    const [horaCampanha, setHoraCampanha] = useState('');
    const [minutoCampanha, setMinutoCampanha] = useState('00');
    const [isFiltroData, setIsFiltroData] = useState(true);
    const [dataInicio, setDataInicio] = useState(new Date().toISOString().split('T')[0]);
    const [dataFinal, setDataFinal] = useState(new Date().toISOString().split('T')[0]);

    const [isChatAtendimento, setIsChatAtendimento] = useState(false);
    const [canalEnvio, setCanalEnvio] = useState('');
    const [sendingType, setSendingType] = useState('whatsapp');

    // Files & Audio
    const [isFile, setIsFile] = useState(false);
    const [tipoArquivo, setTipoArquivo] = useState('');
    const [files, setFiles] = useState([]);
    const [audioGravado, setAudioGravado] = useState(null);
    const [recording, setRecording] = useState(false);
    const [loadingAudio, setLoadingAudio] = useState(false);
    const [estimativa, setEstimativa] = useState(null);
    const [loadingEstimativa, setLoadingEstimativa] = useState(false);

    // Templates WhatsApp Business
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [useTemplate, setUseTemplate] = useState(false);
    const [templateSelecionado, setTemplateSelecionado] = useState(null);
    const [templateVariaveis, setTemplateVariaveis] = useState({});

    // Config Data
    const [configCrm, setConfigCrm] = useState(null);
    const [listaClientesCanais, setListaClientesCanais] = useState([]);
    const [clienteIdPadrao, setClienteIdPadrao] = useState(user.cliente_id_padrao);
    const isMultisession = cliente.multiSession === 1;

    const recorder = useMemo(() => new MicRecorder({ bitRate: 128 }), []);
    const filesUrl = [];

    // Helper Functions para Template
    const normalizarComponents = (components) => {
        if (!Array.isArray(components)) return [];
        return components.map((c) => ({
            ...c,
            type: typeof c.type === 'string' ? c.type.toUpperCase() : c.type,
        }));
    };

    const extrairVariaveisTemplate = (components) => {
        const variaveis = {};
        if (!components || !Array.isArray(components)) return variaveis;

        components.forEach((component) => {
            if (component.text) {
                const matches = component.text.match(/\{\{(\d+)\}\}/g);
                if (matches) {
                    matches.forEach((match) => {
                        const numero = match.replace(/\{\{|\}\}/g, '');
                        variaveis[numero] = '';
                    });
                }
            }
            if (component.buttons && Array.isArray(component.buttons)) {
                component.buttons.forEach((button) => {
                    if (button.text) {
                        const matches = button.text.match(/\{\{(\d+)\}\}/g);
                        if (matches) {
                            matches.forEach((match) => {
                                const numero = match.replace(/\{\{|\}\}/g, '');
                                variaveis[numero] = '';
                            });
                        }
                    }
                })
            }
        });
        return variaveis;
    };

    // Derived State
    const isWhatsAppBusiness = useMemo(() => {
        const selectedChannel = listaClientesCanais.find(c => c.cliente_id === canalEnvio);
        return selectedChannel?.platform === 'whatsapp_business';
    }, [canalEnvio, listaClientesCanais]);

    // --- Effects ---

    useEffect(() => {
        if (user.cliente_id_padrao) {
            setClienteIdPadrao(user.cliente_id_padrao);
        }
        if (isMultisession) {
            buscarListaClientesCanais();
        }
        callListaCampanhas(isMultisession ? clienteIdPadrao : cliente._id);
        buscarConfigCrm(null);
        dispatch(getListaOptionsTags(cliente._id));
        fetchContactLists(cliente._id);
    }, []);

    const fetchContactLists = async (id) => {
        try {
            const res = await v2_actionListarListas(id);
            setContactLists(res.data.data);
        } catch (error) {
            console.error('Error fetching contact lists', error);
        }
    };

    useEffect(() => {
        const getEstimativa = async () => {
            if (activeStep !== 2) return;

            if (tipoCampanha === 'funil' && (!funilId || funilIds.length === 0)) {
                setEstimativa(null);
                return;
            }
            if (tipoCampanha === 'tag' && tagsIds.length === 0) {
                setEstimativa(null);
                return;
            }
            if (tipoCampanha === 'lista_contatos') {
                // Estimativa not supported yet for lists in this effect
                setEstimativa(null);
                return;
            }
            if (tipoCampanha === 'csv') {
                if (csvFile) {
                    validateAndCountCsvUpdated(csvFile);
                } else {
                    setEstimativa(null);
                }
                return;
            }

            setLoadingEstimativa(true);
            try {

                const payload = {
                    funil_ids: tipoCampanha === 'funil' ? funilIds.map(f => f._id) : [],
                    tag_ids: tipoCampanha === 'tag' ? tagsIds.map(t => t._id || t.id) : [],
                    dataInicio,
                    dataFinal,
                };
                const res = await actionCampanhasEstimativa(canalEnvio, payload);
                if (res.data) {
                    const data = res.data.data;
                    if (typeof data === 'object' && data !== null && 'total' in data) {
                        setEstimativa(data.total);
                    } else {
                        setEstimativa(data?.qty || data?.count || data);
                    }
                }
            } catch (error) {
                console.log(error);
                setEstimativa(0);
            } finally {
                setLoadingEstimativa(false);
            }
        };

        const timeoutId = setTimeout(() => {
            getEstimativa();
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [tipoCampanha, funilId, funilIds, tagsIds, isFiltroData, dataInicio, dataFinal, activeStep, clienteIdPadrao, isMultisession, cliente._id, canalEnvio, csvFile]);

    // Buscar Templates
    useEffect(() => {
        const buscarTemplates = async () => {
            if (isWhatsAppBusiness && canalEnvio) {
                setLoadingTemplates(true);
                try {
                    const res = await v1_actionWboTemplates(canalEnvio);
                    // Ajuste conforme o retorno da API, assumindo que retorna { data: { templates: [] } } ou similar
                    setTemplates(res.data?.data?.templates || []);
                } catch (error) {
                    console.log('Erro ao buscar templates:', error);
                } finally {
                    setLoadingTemplates(false);
                }
            }
        };

        buscarTemplates();
    }, [isWhatsAppBusiness, canalEnvio]);

    // Quando mudar canal, limpar template se não for WABA ou se mudar o canal
    useEffect(() => {
        if (!isWhatsAppBusiness) {
            setUseTemplate(false);
            setTemplateSelecionado(null);
            setTemplateVariaveis({});
        } else {
            setUseTemplate(true);
        }
    }, [isWhatsAppBusiness]);


    // --- API Calls ---

    async function buscarListaClientesCanais() {
        try {
            const response = await actionListaClientesCanais(cliente._id);
            const canais = response.data.data;
            setListaClientesCanais(canais);

            // Auto-selecionar o primeiro canal se nenhum estiver selecionado
            if (!clienteIdPadrao && canais.length > 0) {
                const primeiroCanalId = canais[0].cliente_id;
                setClienteIdPadrao(primeiroCanalId);
                callListaCampanhas(primeiroCanalId);
            }
        } catch (err) {
            console.log(err);
        }
    }

    async function buscarConfigCrm(querys) {
        try {
            const result = await v2_actionConfigCrm(cliente._id, querys);
            if (result.data.data) {
                setConfigCrm(result.data.data);
            }
        } catch (error) {
            console.log(error);
        }
    }

    const callListaCampanhas = async (clienteId) => {
        try {
            const res = await actionListaCampanhas(clienteId);
            setTableData(res.data.data);
        } catch (error) {
            console.log('error', error);
        }
    }

    // --- Handlers ---

    const handleChangeClienteIdPadrao = (val) => {
        setClienteIdPadrao(val);
        callListaCampanhas(isMultisession ? val : cliente._id);
    }

    const limparCampos = () => {
        setFunilIds([]);
        setTagsIds([]);
        setEstimativa(null);
        setAudioGravado(null);
        setDataCampanha(new Date().toISOString().split('T')[0]);
        setHoraCampanha('');
        setMinutoCampanha('');
        setNomeCampanha('');
        setTipoCampanha('');
        setBody('');
        setFiles([]);
        setIsEdit(false);
        setIsFile(false);
        setIsChatAtendimento(false);
        setIsFiltroData(true);
        setDataInicio(new Date().toISOString().split('T')[0]);
        setDataFinal(new Date().toISOString().split('T')[0]);
        setFunilId('');
        setActiveStep(1);
        setCanalEnvio(isMultisession ? clienteIdPadrao : '');
        setUseTemplate(false);
        setTemplateSelecionado(null);
        setTemplateVariaveis({});
        setCsvFile(null);
        setSelectedContactLists([]);
    }

    const handleOpenDialog = async (row = null) => {
        limparCampos();
        if (row) {
            setIsEdit(true);
            setCampanhaId(row._id);
            setFunilIds(row.funil_ids_content || []);
            setTagsIds(row.tags_ids_content || []);
            setIsFiltroData(row.isFiltroData);
            setDataInicio(row.data_inicio ? row.data_inicio.split('T')[0] : '');
            setDataFinal(row.data_final ? row.data_final.split('T')[0] : '');
            setCanalEnvio(row.cliente_id || (isMultisession ? clienteIdPadrao : ''));

            try {
                const res = await actionBuscarCampanhaById(row._id);
                if (res.data.data) {
                    const data = res.data.data;
                    let hora = new Date(data.data_envio).getHours();
                    let minuto = new Date(data.data_envio).getMinutes();

                    setIsFile(data.file);
                    setHoraCampanha(hora.toString().padStart(2, '0'));
                    setMinutoCampanha(minuto.toString().padStart(2, '0'));
                    setBody(data.texto);
                    setNomeCampanha(data.nome);
                    setTipoCampanha(data.tipo);
                    setIsChatAtendimento(data.isChatAtendimento);
                    setFunilId(data.funil_id || '');
                    setFunilIds(data.funil_ids_content || []);
                    setTagsIds(data.tags_ids_content || []);
                    setSelectedContactLists(data.contact_list_ids || []);
                    setDataInicio(data.data_inicio ? data.data_inicio.split('T')[0] : '');
                    setDataFinal(data.data_final ? data.data_final.split('T')[0] : '');
                    setCanalEnvio(data.cliente_id || (isMultisession ? clienteIdPadrao : ''));
                    if (data.config && data.config.useTemplate) {
                        setUseTemplate(true);
                        setTemplateSelecionado(data.config.templateSelecionado);
                        setTemplateVariaveis(data.config.templateVariaveis || {});
                    } else if (data.useTemplate) { // Fallback for legacy data (if any)
                        setUseTemplate(true);
                        setTemplateSelecionado(data.templateSelecionado);
                        setTemplateVariaveis(data.templateVariaveis || {});
                    }
                }
            } catch (error) {
                console.log('error', error);
            }
        }
        setDialogOpen(true);
    }

    const handleOpenJobs = async (row) => {
        setLoadingJobs(true);
        setJobsModalOpen(true);
        setJobsData({ jobs: [], summary: {} });
        try {
            const res = await v3_marketing_listarJobs_action(row._id);
            if (res.data.data) {
                setJobsData(res.data.data);
            }
        } catch (error) {
            console.log(error);
            toast({ title: "Erro", description: "Falha ao carregar progresso", variant: "destructive" });
        } finally {
            setLoadingJobs(false);
        }
    }

    const handleDeleteJob = async (jobId) => {
        try {
            await v3_marketing_deletarJob_action(jobId);
            setJobsData(prev => ({
                ...prev,
                jobs: prev.jobs.filter(j => j._id !== jobId),
                summary: {
                    ...prev.summary,
                    total: (prev.summary.total || 0) - 1,   // Roughly update totals
                    agendado: (prev.summary.agendado || 0) - 1 // Assuming we delete mostly scheduled ones
                }
            }));
            toast({ title: "Sucesso", description: "Item removido da lista" });
        } catch (error) {
            console.log(error);
            toast({ title: "Erro", description: "Falha ao remover item", variant: "destructive" });
        }
    }

    const handleNext = () => {
        if (activeStep === 1) {
            if (!nomeCampanha) return toast({ title: "Erro", description: "Informe o nome da campanha", variant: "destructive" });
            if (isMultisession && !canalEnvio) return toast({ title: "Erro", description: "Selecione um canal de envio", variant: "destructive" });
        }
        if (activeStep === 2) {
            if (!tipoCampanha) return toast({ title: "Erro", description: "Selecione o tipo de destinatário", variant: "destructive" });
            if (tipoCampanha === 'funil' && !funilId) return toast({ title: "Erro", description: "Selecione um funil", variant: "destructive" });
            if (tipoCampanha === 'lista_contatos' && selectedContactLists.length === 0) return toast({ title: "Erro", description: "Selecione ao menos uma lista", variant: "destructive" });
            if (tipoCampanha === 'csv' && !csvFile && !isEdit) return toast({ title: "Erro", description: "Faça upload do arquivo CSV", variant: "destructive" });

            if (isFiltroData && (tipoCampanha === 'funil' || tipoCampanha === 'tag')) {
                if (!dataInicio || !dataFinal) return toast({ title: "Erro", description: "Informe as datas do filtro", variant: "destructive" });
            }
        }
        if (activeStep === 3) {
            // Se usar template, deve ter um selecionado.
            if (useTemplate && !templateSelecionado) {
                return toast({ title: "Erro", description: "Selecione um template.", variant: "destructive" });
            }
            // Se NÃO usar template, deve ter body ou file.
            // Se usar template, body e file são opcionais (pode ir junto ou não).
            // Logo, se NÃO usar template E NÃO tiver body nem file, erro.
            if (!useTemplate && !body && !isFile) {
                return toast({ title: "Atenção", description: "Sua campanha não tem mensagem nem arquivo.", variant: "warning" });
            }
            if (!useTemplate && isFile && files.length === 0 && !audioGravado) {
                return toast({ title: "Atenção", description: "Você selecionou enviar mídia, mas não anexou nenhum arquivo ou áudio.", variant: "warning" });
            }
        }

        setActiveStep(prev => prev + 1);
    }

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
    }

    const handleFileChange = (e) => {
        if (e.target.files) {
            setFiles(Array.from(e.target.files));
        }
    }

    async function uploadAndGetUrl() {
        if (files.length > 0) {
            for (let file of files) {
                var formData = new FormData();
                formData.append("file", file);
                try {
                    let querys = `?path=plataforma/campanhas`;
                    const response = await v2_actionUploadS3(formData, querys);
                    if (!response.data.error) {
                        filesUrl.push({ key: response.data.data.key, type: file.type });
                    }
                } catch (error) {
                    console.log('erro ao upload arquivo', error);
                }
            }
        }
    }

    async function uploadAudioAndGetUrl(file) {
        if (file) {
            var formData = new FormData();
            formData.append("file", file);
            try {
                let querys = `?path=plataforma/agendamentos`;
                const response = await v2_actionUploadS3(formData, querys);
                if (!response.data.error) {
                    return { key: response.data.data.key, type: file.type, tipo: "audio" };
                }
            } catch (error) {
                console.log('erro ao upload arquivo', error);
            }
        }
    }

    const handleSelecionarTemplate = (templateId) => {
        if (!templateId || templateId === "none") {
            setTemplateSelecionado(null);
            setTemplateVariaveis({});
            return;
        }

        const template = templates.find(t => (t.templateId || t._id) === templateId);
        if (!template) return;

        const comps = normalizarComponents(template?.components || []);
        const conteudo = comps?.find(c => c.type === 'BODY')?.text || '';
        const variaveis = extrairVariaveisTemplate(comps);

        setTemplateSelecionado({
            templateId: template.templateId || template._id,
            name: template.name || template.templateName,
            conteudo: conteudo,
            components: comps
        });
        setTemplateVariaveis(variaveis);
    };

    const handleAtualizarVariavelTemplate = (key, value) => {
        setTemplateVariaveis(prev => ({
            ...prev,
            [key]: value
        }));
    };

    // --- Audio ---
    const handleStartRecording = async () => {
        setLoadingAudio(true);
        try {
            await navigator.mediaDevices.getUserMedia({ audio: true });
            await recorder.start();
            setRecording(true);
            setLoadingAudio(false);
        } catch (err) {
            console.log(err);
            setLoadingAudio(false);
        }
    };

    const validateAndCountCsvUpdated = (file) => {
        if (!file) {
            setEstimativa(null);
            return;
        }
        setLoadingEstimativa(true);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const text = e.target.result;
                const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');

                if (lines.length < 2) {
                    setEstimativa(0);
                    setLoadingEstimativa(false);
                    setCsvFile(null);
                    return;
                }

                // Handle BOM
                let headerLine = lines[0].trim();
                if (headerLine.charCodeAt(0) === 0xFEFF) {
                    headerLine = headerLine.substr(1);
                }

                const headers = headerLine.toLowerCase().replace(/"/g, '').split(/[;,\t]/).map(h => h.trim());
                console.log('CSV Headers Found:', headers);

                const phoneIdx = headers.findIndex(h => h.includes('numero') || h.includes('phone') || h.includes('telefone') || h.includes('celular') || h.includes('mobile'));

                if (phoneIdx === -1) {
                    toast({ title: "Erro no CSV", description: "Coluna 'numero', 'telefone' ou 'phone' não encontrada.", variant: "destructive" });
                    setEstimativa(0);
                    setLoadingEstimativa(false);
                    setCsvFile(null);
                    return;
                }

                let count = 0;
                for (let i = 1; i < lines.length; i++) {
                    const line = lines[i].trim();
                    if (!line) continue;

                    const cols = line.split(/[;,\t]/);
                    const rawPhone = cols[phoneIdx];

                    if (rawPhone) {
                        const phone = rawPhone.replace(/\D/g, '');
                        // Validate Code + DDD + Number (at least 10 digits)
                        if (phone.length >= 10) count++;
                    }
                }
                setEstimativa(count);
                toast({ title: "Arquivo Analisado", description: `${count} contatos válidos identificados.` });
            } catch (err) {
                console.error("CSV Parse Error", err);
                toast({ title: "Erro", description: "Falha ao processar arquivo CSV", variant: "destructive" });
                setEstimativa(0);
                setCsvFile(null);
            } finally {
                setLoadingEstimativa(false);
            }
        };
        reader.onerror = () => {
            toast({ title: "Erro", description: "Erro ao ler arquivo", variant: "destructive" });
            setLoadingEstimativa(false);
            setCsvFile(null);
        }
        reader.readAsText(file);
    };


    const handleStopRecording = async () => {
        try {
            const [buffer, blob] = await recorder.stop().getMp3();
            const file = new File(buffer, "audio.mp3", {
                type: blob.type,
                lastModified: Date.now()
            });
            setAudioGravado(file);
            setRecording(false);
        } catch (error) {
            console.log(error);
        }
    }

    const validateAndCountCsv = (file) => {
        if (!file) {
            setEstimativa(null);
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const lines = text.split(/\r?\n/);
            let count = 0;

            if (lines.length < 2) {
                setEstimativa(0);
                return;
            }
            const headers = lines[0].toLowerCase().replace(/"/g, '').split(/[;,]/).map(h => h.trim());
            const phoneIdx = headers.findIndex(h => h.includes('numero') || h.includes('phone') || h.includes('telefone') || h.includes('celular'));

            if (phoneIdx === -1) {
                toast({ title: "Erro no CSV", description: "Coluna 'numero' não encontrada.", variant: "destructive" });
                setEstimativa(0);
                return;
            }

            for (let i = 1; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue;
                // Basic split, might fail on quoted commas but sufficient for simple "numero,nome"
                const cols = line.split(/[;,]/);
                const phone = cols[phoneIdx] ? cols[phoneIdx].replace(/\D/g, '') : '';
                // Validate Code + DDD + Number (at least 10 digits for Brazil, or generic rule)
                if (phone.length >= 10) count++;
            }
            setEstimativa(count);
            // toast({ title: "CSV Analisado", description: `${count} contatos válidos encontrados.` });
        };
        reader.readAsText(file);
    }

    // --- Submit ---
    const onSubmit = async () => {
        if (!nomeCampanha) {
            return toast({ title: "Campo Obrigatório", description: "Informe o nome da campanha (Passo 1).", variant: "destructive" });
        }
        if (horaCampanha === "" || minutoCampanha === "") {
            return toast({ title: "Campo Obrigatório", description: "Defina o horário do envio (Passo 4).", variant: "destructive" });
        }
        // Sanity Check
        if (!useTemplate && isFile && files.length === 0 && !audioGravado) {
            return toast({ title: "Arquivo Ausente", description: "Você marcou 'Enviar Mídia' mas não anexou nenhum arquivo.", variant: "destructive" });
        }

        setLoadingEnviarArquivos(true);
        try {
            // Logic to prepare payload
            const payload = {
                nome: nomeCampanha,
                dataCampanha: new Date(dataCampanha),
                horaCampanha,
                minutoCampanha,
                funilIds,
                tagsIds,
                isFiltroData,
                dataInicio,
                dataFinal,
                status_envio: "agendado",
                criado_por: user.id,
                cliente_id: isMultisession ? canalEnvio : cliente._id,
                tipo: tipoCampanha,
                texto: body,
                isFile,
                files: filesUrl,
                isChatAtendimento,
                useTemplate,
                templateSelecionado,
                templateVariaveis,
                contactListIds: selectedContactLists.map(l => l._id || l)
            }

            if (tipoCampanha === 'csv' && csvFile) {
                const formData = new FormData();
                formData.append("file", csvFile);
                let querys = `?path=plataforma/campanhas/csv`;
                const result = await v2_actionUploadS3(formData, querys);
                if (!result.data.error) {
                    payload.csvUrl = result.data.data.location || result.data.data.url || (import.meta.env.VITE_AWS_URL + "/" + result.data.data.key);
                    // If location is returned by backend (as seen in s3 controller), use it.
                    if (result.data.data.location) payload.csvUrl = result.data.data.location;
                } else {
                    throw new Error("Erro ao fazer upload do CSV");
                }
            }

            if (isFile) {
                if (tipoArquivo === "audio" && audioGravado) {
                    const audio = await uploadAudioAndGetUrl(audioGravado);
                    if (audio) payload.files = [audio];
                } else if (files.length > 0) {
                    await uploadAndGetUrl();
                    payload.files = filesUrl;
                }
            }

            if (isEdit) {
                await actionEditarCampanha(campanhaId, { ...payload, action: isFile ? 'edit_info_with_file' : 'edit_info_without_file' });
            } else {
                await v3_marketing_criarCampanhas_action(payload);
            }

            setDialogOpen(false);
            // Recarregar listagem usando o canal selecionado no modal
            const canalParaRecarregar = isMultisession ? (canalEnvio || clienteIdPadrao) : cliente._id;
            if (isMultisession && canalEnvio && canalEnvio !== clienteIdPadrao) {
                setClienteIdPadrao(canalEnvio);
            }
            callListaCampanhas(canalParaRecarregar);
            limparCampos();
            toast({ title: "Sucesso", description: `Campanha ${isEdit ? 'editada' : 'criada'} com sucesso.` });

        } catch (error) {
            console.log(error);
            toast({ title: "Erro", description: "Falha ao salvar campanha", variant: "destructive" });
        } finally {
            setLoadingEnviarArquivos(false);
        }
    }

    const onDelete = async () => {
        try {
            await actionDescontinuarcampanha(campanhaId);
            setModalConfirmarExclusao(false);
            callListaCampanhas(isMultisession ? clienteIdPadrao : cliente._id);
            toast({ title: "Sucesso", description: "Campanha excluída" });
        } catch (error) {
            console.log(error);
            toast({ title: "Erro", description: "Falha ao excluir campanha", variant: "destructive" });
        }
    }

    // Filter Logic
    const dataFiltered = tableData.filter((item) => {
        if (currentTab !== 'all' && item.status_envio !== currentTab) return false;
        if (filterName && !item.nome.toLowerCase().includes(filterName.toLowerCase())) return false;
        return true;
    });

    return (
        <div className={CAMPANHAS_DESIGN.page.container}>
            {/* Sticky Header */}
            <div className={CAMPANHAS_DESIGN.header.container}>
                <div className={CAMPANHAS_DESIGN.header.wrapper}>

                    {/* Left: Title & Filters */}
                    <div className={CAMPANHAS_DESIGN.header.left}>
                        <div className="flex items-center gap-2">
                            <div className={CAMPANHAS_DESIGN.header.iconContainer}>
                                <Iconify icon="mdi:bullhorn" className={CAMPANHAS_DESIGN.header.icon} width={20} />
                            </div>
                            <div>
                                <h3 className={CAMPANHAS_DESIGN.header.title}>Campanhas</h3>
                                <p className={CAMPANHAS_DESIGN.header.subtitle}>Crie e programe novidades</p>
                            </div>
                        </div>

                        <div className={CAMPANHAS_DESIGN.header.divider} />

                        {/* Filters */}
                        <div className={CAMPANHAS_DESIGN.header.filters}>
                            {isMultisession && (
                                <div className={CAMPANHAS_DESIGN.filters.selectContainer}>
                                    <span className={CAMPANHAS_DESIGN.filters.label}>Canal</span>
                                    <Select value={clienteIdPadrao} onValueChange={handleChangeClienteIdPadrao}>
                                        <SelectTrigger className="h-9">
                                            <SelectValue placeholder="Canal" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {listaClientesCanais.map(c => (
                                                <SelectItem key={c.cliente_id} value={c.cliente_id}>{c.nome}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            <div className={CAMPANHAS_DESIGN.filters.selectContainer}>
                                <span className={CAMPANHAS_DESIGN.filters.label}>Status</span>
                                <Select value={currentTab} onValueChange={setCurrentTab}>
                                    <SelectTrigger className="h-9 min-w-[140px]">
                                        <SelectValue placeholder="Status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {STATUS_OPTIONS.map(opt => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className={CAMPANHAS_DESIGN.filters.searchContainer}>
                                <div className={CAMPANHAS_DESIGN.filters.searchIcon}>
                                    <Search size={16} />
                                </div>
                                <Input
                                    placeholder="Pesquisar..."
                                    value={filterName}
                                    onChange={e => setFilterName(e.target.value)}
                                    className={CAMPANHAS_DESIGN.filters.input}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right: Actions */}
                    <div className={CAMPANHAS_DESIGN.header.right}>
                        <Button onClick={() => handleOpenDialog()} className="bg-[#7C3AED] hover:bg-[#6d28d9]">
                            <Plus size={16} className="mr-2" />
                            Criar Campanha
                        </Button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className={CAMPANHAS_DESIGN.page.contentWrapper}>
                <div className={CAMPANHAS_DESIGN.page.card}>

                    {/* Table */}
                    <div className="flex-1 overflow-auto p-0 flex flex-col">
                        {(isMultisession && !clienteIdPadrao) ? (
                            <div className="flex flex-col items-center justify-center flex-1 p-8 text-gray-500">
                                <div className="p-4 rounded-full bg-gray-50 mb-4">
                                    <Iconify icon="mdi:store-search" width={40} className="text-gray-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-700">Selecione um Canal</h3>
                                <p className="text-sm text-gray-500 text-center max-w-sm">
                                    Para visualizar e gerenciar suas campanhas, selecione um canal de atendimento acima.
                                </p>
                            </div>
                        ) : dataFiltered.length === 0 ? (
                            <div className="flex flex-col items-center justify-center flex-1 p-8 text-gray-500">
                                <div className="p-4 rounded-full bg-gray-50 mb-4">
                                    <Iconify icon="mdi:clipboard-text-off-outline" width={40} className="text-gray-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-700">Nenhuma campanha encontrada</h3>
                                <p className="text-sm text-gray-500 text-center max-w-sm mb-6">
                                    {filterName || currentTab !== 'all'
                                        ? "Nenhum resultado corresponde aos filtros aplicados."
                                        : "Você ainda não tem campanhas criadas neste canal."}
                                </p>
                                {(!filterName && currentTab === 'all') && (
                                    <Button onClick={() => handleOpenDialog()} className="bg-[#7C3AED] hover:bg-[#6d28d9]">
                                        <Plus size={16} className="mr-2" />
                                        Criar Primeira Campanha
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <Table>
                                <TableHeader className={CAMPANHAS_DESIGN.table.header}>
                                    <TableRow>
                                        {TABLE_HEAD.map((head) => (
                                            <TableHead key={head.id} className={CAMPANHAS_DESIGN.table.headCell}>{head.label}</TableHead>
                                        ))}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {dataFiltered.map((row) => (
                                        <TableRow key={row._id} className={CAMPANHAS_DESIGN.table.row}>
                                            <TableCell className={CAMPANHAS_DESIGN.table.cell}>
                                                <span className="font-medium">{row.nome}</span>
                                            </TableCell>
                                            <TableCell className={CAMPANHAS_DESIGN.table.cell}>
                                                <Badge variant={row.status_envio === 'agendado' ? 'secondary' : 'default'} className="uppercase">
                                                    {row.status_envio}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className={CAMPANHAS_DESIGN.table.cell}>{row.criado_por_nome || row.criado_por}</TableCell>
                                            <TableCell className={CAMPANHAS_DESIGN.table.cell}>
                                                {row.createdAt ? fDateTimeSuffix(row.createdAt) : '-'}
                                            </TableCell>
                                            <TableCell className={CAMPANHAS_DESIGN.table.cell}>{row.tipo}</TableCell>
                                            <TableCell className={CAMPANHAS_DESIGN.table.cell}>
                                                {row.data_envio ? fDateTimeSuffix(row.data_envio) : '-'}
                                            </TableCell>
                                            <TableCell className={CAMPANHAS_DESIGN.table.cell}>
                                                <Switch checked={row.status === 'ativo'} />
                                            </TableCell>
                                            <TableCell className={CAMPANHAS_DESIGN.table.cell}>
                                                <div className="flex items-center justify-end gap-2">
                                                    <Button variant="ghost" size="icon" onClick={() => handleOpenJobs(row)} title="Ver Progresso">
                                                        <Eye size={16} className="text-blue-500" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(row)}>
                                                        <Edit2 size={16} className="text-gray-500" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" onClick={() => { setCampanhaId(row._id); setModalConfirmarExclusao(true); }}>
                                                        <Trash2 size={16} className="text-red-500" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                </div>
            </div>

            {/* Wizard Modal for Create/Edit */}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) limparCampos();
            }}>
                <DialogContent className={CAMPANHAS_DESIGN.dialog.content}>
                    <DialogHeader className={CAMPANHAS_DESIGN.dialog.header}>
                        <DialogTitle className={CAMPANHAS_DESIGN.dialog.title}>{isEdit ? 'Editar Campanha' : 'Nova Campanha'}</DialogTitle>
                    </DialogHeader>

                    {/* Stepper Header - Simple */}
                    <div className="bg-white border-b border-gray-100 py-6 px-10">
                        <div className="relative">
                            {/* Line Background */}
                            <div className="absolute top-[14px] left-0 w-full h-[2px] bg-gray-100 -z-0" />
                            {/* Line Progress */}
                            <div
                                className="absolute top-[14px] left-0 h-[2px] bg-[#7C3AED] transition-all duration-500 ease-in-out -z-0"
                                style={{ width: `${((activeStep - 1) / (STEPS.length - 1)) * 100}%` }}
                            />

                            <div className="flex justify-between relative z-10">
                                {STEPS.map((step) => {
                                    const isActive = step.id === activeStep;
                                    const isCompleted = step.id < activeStep;

                                    return (
                                        <div key={step.id} className="flex flex-col items-center gap-2 bg-white px-2">
                                            <div
                                                className={cn(
                                                    "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 border-2",
                                                    isActive ? "bg-[#7C3AED] border-[#7C3AED] text-white scale-110" :
                                                        (isCompleted ? "bg-green-500 border-green-500 text-white" : "bg-white border-gray-200 text-gray-300")
                                                )}
                                            >
                                                {isCompleted ? <Iconify icon="eva:checkmark-fill" width={14} /> : step.id}
                                            </div>
                                            <span className={cn(
                                                "text-[10px] font-semibold uppercase tracking-wider transition-colors",
                                                isActive ? "text-[#7C3AED]" : (isCompleted ? "text-green-600" : "text-gray-400")
                                            )}>
                                                {step.label}
                                            </span>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>

                    <div className={CAMPANHAS_DESIGN.dialog.body}>

                        {/* STEP 1: Identificação */}
                        {activeStep === 1 && (
                            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                                <div>

                                    <div className={CAMPANHAS_DESIGN.dialog.formGrid}>
                                        <div>
                                            <Label className={CAMPANHAS_DESIGN.dialog.label}>Nome da Campanha</Label>
                                            <Input
                                                value={nomeCampanha}
                                                onChange={e => setNomeCampanha(e.target.value)}
                                            />
                                        </div>

                                        <div className="mb-4">
                                            <Label className={CAMPANHAS_DESIGN.dialog.label}>Tipo de Envio</Label>
                                            <RadioGroup value={sendingType} onValueChange={setSendingType} className="grid grid-cols-2 gap-4" disabled={isEdit}>
                                                <label
                                                    className={cn(
                                                        "flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all",
                                                        sendingType === 'whatsapp'
                                                            ? "border-green-500 bg-green-50 ring-1 ring-green-500"
                                                            : "border-gray-200 hover:border-gray-300",
                                                        isEdit && "opacity-50 cursor-not-allowed"
                                                    )}
                                                >
                                                    <RadioGroupItem value="whatsapp" id="type-whatsapp" className="sr-only" />
                                                    <div className={cn("p-2 rounded-full", sendingType === 'whatsapp' ? "bg-green-200 text-green-700" : "bg-gray-100 text-gray-500")}>
                                                        <Iconify icon="mdi:whatsapp" width={20} />
                                                    </div>
                                                    <span className="font-medium text-sm text-gray-700">WhatsApp</span>
                                                </label>

                                                <label
                                                    className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50 cursor-not-allowed opacity-70"
                                                >
                                                    <RadioGroupItem value="sms" id="type-sms" disabled className="sr-only" />
                                                    <div className="p-2 rounded-full bg-gray-100 text-gray-400">
                                                        <Iconify icon="mdi:message-processing" width={20} />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="font-medium text-sm text-gray-500">SMS</span>
                                                        <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">(Em breve)</span>
                                                    </div>
                                                </label>
                                            </RadioGroup>
                                        </div>

                                        {isMultisession && (
                                            <div>
                                                <Label className={CAMPANHAS_DESIGN.dialog.label}>Canal de Envio</Label>
                                                <Select value={canalEnvio} onValueChange={setCanalEnvio} disabled={isEdit}>
                                                    <SelectTrigger className="bg-white">
                                                        <SelectValue placeholder="Selecione o canal" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {listaClientesCanais.map(c => (
                                                            <SelectItem key={c.cliente_id} value={c.cliente_id}>
                                                                <span className="flex items-center gap-2">
                                                                    {c.nome}
                                                                    <span className="text-xs text-muted-foreground bg-gray-100 px-1.5 py-0.5 rounded capitalize">
                                                                        {c.platform === 'whatsapp' ? 'WhatsApp' : (c.platform === 'whatsapp_business' ? 'WABA' : c.platform)}
                                                                    </span>
                                                                </span>
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                {listaClientesCanais.find(c => c.cliente_id === canalEnvio)?.platform === 'whatsapp' && (
                                                    <div className="mt-3 p-3 bg-orange-50 border border-orange-100 rounded-lg flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                                                        <AlertCircle className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
                                                        <div className="text-sm text-orange-700">
                                                            <strong className="font-semibold block mb-0.5">Alto Risco de Banimento</strong>
                                                            O uso de disparos em massa via WhatsApp convencional viola os termos de uso e pode resultar no bloqueio permanente do seu número. Recomendamos utilizar a API Oficial.
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-between hover:bg-gray-100 transition-colors cursor-pointer" onClick={() => setIsChatAtendimento(!isChatAtendimento)}>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-semibold text-gray-900">Chat de Atendimento</span>
                                                <span className="text-xs text-gray-500">Habilitar chat para atendimento humano?</span>
                                            </div>
                                            <Switch checked={isChatAtendimento} onCheckedChange={setIsChatAtendimento} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STEP 2: Destinatários & Filtros */}
                        {activeStep === 2 && (
                            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">


                                <RadioGroup value={tipoCampanha} onValueChange={setTipoCampanha} className="grid grid-cols-3 gap-3 mb-4" disabled={isEdit}>
                                    <div
                                        className={cn(
                                            "flex flex-row items-center justify-start p-3 border rounded-lg cursor-pointer transition-all duration-200 gap-3",
                                            tipoCampanha === 'funil'
                                                ? "border-[#7C3AED] bg-violet-50/50 shadow-sm"
                                                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50",
                                            isEdit && "opacity-60 cursor-not-allowed pointer-events-none"
                                        )}
                                        onClick={() => !isEdit && setTipoCampanha('funil')}
                                    >
                                        <RadioGroupItem value="funil" id="r-funil" className="sr-only" />
                                        <div className={cn("p-1.5 rounded-full shrink-0 transition-colors", tipoCampanha === 'funil' ? "bg-[#7C3AED] text-white" : "bg-gray-100 text-gray-400")}>
                                            <Iconify icon="mdi:filter-outline" width={18} />
                                        </div>
                                        <div className="flex flex-col items-start">
                                            <span className={cn("font-bold text-xs", tipoCampanha === 'funil' ? "text-[#7C3AED]" : "text-gray-600")}>Funil de Vendas</span>
                                            <span className="text-[10px] text-gray-400 leading-tight">Por estágio do funil</span>
                                        </div>
                                    </div>

                                    <div
                                        className={cn(
                                            "flex flex-row items-center justify-start p-3 border rounded-lg cursor-pointer transition-all duration-200 gap-3",
                                            tipoCampanha === 'tag'
                                                ? "border-[#7C3AED] bg-violet-50/50 shadow-sm"
                                                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50",
                                            isEdit && "opacity-60 cursor-not-allowed pointer-events-none"
                                        )}
                                        onClick={() => !isEdit && setTipoCampanha('tag')}
                                    >
                                        <RadioGroupItem value="tag" id="r-tag" className="sr-only" />
                                        <div className={cn("p-1.5 rounded-full shrink-0 transition-colors", tipoCampanha === 'tag' ? "bg-[#7C3AED] text-white" : "bg-gray-100 text-gray-400")}>
                                            <Iconify icon="mdi:tag-outline" width={18} />
                                        </div>
                                        <div className="flex flex-col items-start">
                                            <span className={cn("font-bold text-xs", tipoCampanha === 'tag' ? "text-[#7C3AED]" : "text-gray-600")}>Por Tags</span>
                                            <span className="text-[10px] text-gray-400 leading-tight">Por etiquetas</span>
                                        </div>
                                    </div>

                                    {isWhatsAppBusiness && (
                                        <>
                                            {/* <div
                                                className={cn(
                                                    "flex flex-row items-center justify-start p-3 border rounded-lg cursor-pointer transition-all duration-200 gap-3",
                                                    tipoCampanha === 'lista_contatos'
                                                        ? "border-[#7C3AED] bg-violet-50/50 shadow-sm"
                                                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50",
                                                    isEdit && "opacity-60 cursor-not-allowed pointer-events-none"
                                                )}
                                                onClick={() => !isEdit && setTipoCampanha('lista_contatos')}
                                            >
                                                <RadioGroupItem value="lista_contatos" id="r-lista" className="sr-only" />
                                                <div className={cn("p-1.5 rounded-full shrink-0 transition-colors", tipoCampanha === 'lista_contatos' ? "bg-[#7C3AED] text-white" : "bg-gray-100 text-gray-400")}>
                                                    <Iconify icon="fluent:person-giggle-20-regular" width={18} />
                                                </div>
                                                <div className="flex flex-col items-start">
                                                    <span className={cn("font-bold text-xs", tipoCampanha === 'lista_contatos' ? "text-[#7C3AED]" : "text-gray-600")}>Listas de Contato</span>
                                                    <span className="text-[10px] text-gray-400 leading-tight">Por listas criadas</span>
                                                </div>
                                            </div> */}

                                            <div
                                                className={cn(
                                                    "flex flex-row items-center justify-start p-3 border rounded-lg cursor-pointer transition-all duration-200 gap-3",
                                                    tipoCampanha === 'csv'
                                                        ? "border-[#7C3AED] bg-violet-50/50 shadow-sm"
                                                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50",
                                                    isEdit && "opacity-60 cursor-not-allowed pointer-events-none"
                                                )}
                                                onClick={() => !isEdit && setTipoCampanha('csv')}
                                            >
                                                <RadioGroupItem value="csv" id="r-csv" className="sr-only" />
                                                <div className={cn("p-1.5 rounded-full shrink-0 transition-colors", tipoCampanha === 'csv' ? "bg-[#7C3AED] text-white" : "bg-gray-100 text-gray-400")}>
                                                    <Iconify icon="mdi:file-delimited-outline" width={18} />
                                                </div>
                                                <div className="flex flex-col items-start">
                                                    <span className={cn("font-bold text-xs", tipoCampanha === 'csv' ? "text-[#7C3AED]" : "text-gray-600")}>Importar CSV</span>
                                                    <span className="text-[10px] text-gray-400 leading-tight">Planilha de contatos</span>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                </RadioGroup>

                                {/* Funil Logic */}
                                {tipoCampanha === 'funil' && (
                                    <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-100">
                                        <div>
                                            <Label className={CAMPANHAS_DESIGN.dialog.label}>Selecionar Funil</Label>
                                            <Select value={funilId} disabled={isEdit} onValueChange={val => {
                                                setFunilId(val);
                                                buscarConfigCrm(`?pipeline_id=${val}`);
                                            }}>
                                                <SelectTrigger className="bg-white">
                                                    <SelectValue placeholder="Selecione um funil..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {configCrm?.pipelines?.map(p => (
                                                        <SelectItem key={p._id} value={p._id}>{p.nome}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        {funilId && (
                                            <div>
                                                <Label className={CAMPANHAS_DESIGN.dialog.label}>Estágios do Funil</Label>
                                                <MultiSelect
                                                    options={configCrm?.stages?.map(s => ({ label: s.nome, value: s._id })) || []}
                                                    defaultValue={funilIds.map(f => f._id)}
                                                    onValueChange={(vals) => {
                                                        const selected = configCrm?.stages?.filter(s => vals.includes(s._id));
                                                        setFunilIds(selected);
                                                    }}
                                                    placeholder="Selecione os estágios"
                                                    className="bg-white"
                                                    disabled={isEdit}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Tag Logic */}
                                {tipoCampanha === 'tag' && (
                                    <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-100">
                                        <Label className={CAMPANHAS_DESIGN.dialog.label}>Selecionar Tags</Label>
                                        <MultiSelect
                                            options={optionsTags?.map(t => ({ label: t.nome, value: t._id || t.id })) || []}
                                            defaultValue={tagsIds.map(t => t._id || t.id)}
                                            onValueChange={(vals) => {
                                                const selected = optionsTags?.filter(t => vals.includes(t._id || t.id));
                                                setTagsIds(selected);
                                            }}
                                            placeholder="Selecione as tags"
                                            className="bg-white"
                                            disabled={isEdit}
                                        />
                                    </div>
                                )}

                                {/* Lists Logic */}
                                {tipoCampanha === 'lista_contatos' && (
                                    <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-100">
                                        <Label className={CAMPANHAS_DESIGN.dialog.label}>Selecionar Listas</Label>
                                        <MultiSelect
                                            options={contactLists.map(list => ({ label: list.nome, value: list._id }))}
                                            defaultValue={selectedContactLists.map(l => l._id || l)}
                                            onValueChange={(vals) => {
                                                const selected = contactLists.filter(l => vals.includes(l._id));
                                                setSelectedContactLists(selected);
                                            }}
                                            placeholder="Selecione as listas"
                                            className="bg-white"
                                            disabled={isEdit}
                                        />
                                    </div>
                                )}

                                {/* CSV Logic */}
                                {tipoCampanha === 'csv' && (
                                    <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-100">
                                        <Label className={CAMPANHAS_DESIGN.dialog.label}>Upload de Planilha (CSV)</Label>
                                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:bg-gray-100 transition-colors bg-white">
                                            <Input
                                                type="file"
                                                accept=".csv"
                                                onChange={(e) => {
                                                    const f = e.target.files[0];
                                                    setCsvFile(f);
                                                    // validateAndCountCsvUpdated is triggered by useEffect logic (if added) or we call it here?
                                                    // We added csvFile to useEffect previously.
                                                    // BUT we must ensure the useEffect calls the NEW function.
                                                }}
                                                className="hidden"
                                                id="csv-upload"
                                                disabled={isEdit}
                                            />
                                            <label htmlFor="csv-upload" className={cn("flex flex-col items-center gap-2", isEdit ? "cursor-not-allowed" : "cursor-pointer")}>
                                                <Iconify icon="mdi:file-excel-outline" width={32} className={isEdit ? "text-gray-300" : "text-green-600"} />
                                                <span className={cn("text-sm font-medium", isEdit ? "text-gray-400" : "text-gray-600")}>
                                                    {csvFile ? csvFile.name : (isEdit ? "Upload bloqueado na edição" : "Clique para selecionar o arquivo .csv")}
                                                </span>
                                            </label>
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            <p className="font-semibold">Modelo esperado:</p>
                                            <p>Colunas: <code>numero, nome</code></p>
                                            <p>Formato número: Codigo Pais + DDD + Numero (ex: 5511999999999)</p>
                                        </div>
                                    </div>
                                )}

                                {(tipoCampanha && tipoCampanha !== 'csv' && tipoCampanha !== 'lista_contatos') && (
                                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                        <Separator className="my-6" />

                                        <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100 mb-4">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Iconify icon="mdi:calendar-clock" className="text-blue-600" width={18} />
                                                <Label className="text-sm font-semibold text-blue-900">Período de Atividade do Chat</Label>
                                            </div>
                                            <p className="text-xs text-blue-600/80 mb-4 leading-relaxed">
                                                Defina o intervalo de tempo para considerar a <strong>última interação</strong> dos clientes.
                                                Apenas clientes que interagiram neste período serão incluídos.
                                            </p>

                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <Label className={cn(CAMPANHAS_DESIGN.dialog.label, "text-xs mb-1.5")}>Data Inicial da Atividade</Label>
                                                    <Input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} className="bg-white" disabled={isEdit} />
                                                </div>
                                                <div>
                                                    <Label className={cn(CAMPANHAS_DESIGN.dialog.label, "text-xs mb-1.5")}>Data Final da Atividade</Label>
                                                    <Input type="date" value={dataFinal} onChange={e => setDataFinal(e.target.value)} className="bg-white" disabled={isEdit} />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Estimativa de Contatos */}
                                        <div className="mt-4 p-4 bg-violet-50 border border-violet-100 rounded-lg flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-white rounded-full text-[#7C3AED]">
                                                    <Iconify icon="mdi:account-group" width={24} />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-violet-900">Estimativa de envio</p>
                                                    <p className="text-xs text-violet-600">Número aproximado de contatos alcançados</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                {loadingEstimativa ? (
                                                    <div className="flex items-center gap-2">
                                                        <span className="w-2 h-2 bg-[#7C3AED] rounded-full animate-bounce" />
                                                        <span className="w-2 h-2 bg-[#7C3AED] rounded-full animate-bounce delay-100" />
                                                        <span className="w-2 h-2 bg-[#7C3AED] rounded-full animate-bounce delay-200" />
                                                    </div>
                                                ) : (
                                                    <span className="text-2xl font-bold text-[#7C3AED]">{estimativa !== null ? estimativa : '-'}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                            </div>
                        )}

                        {/* STEP 3: Conteúdo */}
                        {activeStep === 3 && (
                            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                                <h3 className={CAMPANHAS_DESIGN.dialog.sectionTitle}>Conteúdo da Mensagem</h3>
                                <p className={CAMPANHAS_DESIGN.dialog.sectionSubtitle}>Crie o conteúdo que será enviado.</p>

                                {/* WABA & No File: Side by Side */}
                                {(isWhatsAppBusiness && !isFile) ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                                        {/* Left: Template */}
                                        <div className="flex flex-col gap-4">
                                            <div className="p-4 bg-green-50 border border-green-100 rounded-lg h-full">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex flex-col">
                                                        <Label className="text-green-900 font-medium">Usar Template (WhatsApp)</Label>
                                                        <span className="text-xs text-green-700">Obrigatório para contatos fora da janela de 24h</span>
                                                    </div>
                                                    <Switch checked={useTemplate} onCheckedChange={setUseTemplate} disabled={true} />
                                                </div>

                                                {/* Select Template */}
                                                <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                                                    <div>
                                                        <Label className={CAMPANHAS_DESIGN.dialog.label}>Selecione o Template</Label>
                                                        <Select
                                                            value={templateSelecionado?.templateId || templateSelecionado?._id || ''}
                                                            onValueChange={handleSelecionarTemplate}
                                                            disabled={loadingTemplates}
                                                        >
                                                            <SelectTrigger className="bg-white">
                                                                <SelectValue placeholder={loadingTemplates ? "Carregando templates..." : "Selecione um modelo"} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {templates.filter(t => t.status === 'APPROVED').map((t) => (
                                                                    <SelectItem key={t.templateId || t._id} value={t.templateId || t._id}>
                                                                        {t.templateName}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>

                                                    {/* Variable Inputs */}
                                                    {templateSelecionado && (
                                                        <div className="space-y-4">
                                                            <div className="p-3 bg-white rounded border border-gray-200 text-sm whitespace-pre-wrap max-h-[250px] overflow-y-auto scrollbar-thin">
                                                                <span className="font-semibold text-xs text-gray-500 block mb-1">Pré-visualização:</span>
                                                                {templateSelecionado.conteudo}
                                                            </div>

                                                            {Object.keys(templateVariaveis).length > 0 && (
                                                                <div className="grid grid-cols-1 gap-3">
                                                                    <Label className="text-xs font-semibold uppercase text-gray-500">Variáveis</Label>
                                                                    {Object.keys(templateVariaveis).map((key) => (
                                                                        <div key={key}>
                                                                            <Label className="text-xs text-gray-600 mb-1">Var {key} {'{{' + key + '}}'}</Label>
                                                                            <Input
                                                                                value={templateVariaveis[key]}
                                                                                onChange={(e) => handleAtualizarVariavelTemplate(key, e.target.value)}
                                                                                placeholder={`Valor para {{${key}}}`}
                                                                                className="bg-white"
                                                                            />
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right: Text Message */}
                                        <div className="flex flex-col gap-4">
                                            <div className="p-4 bg-white border border-gray-200 rounded-lg h-full flex flex-col shadow-sm">
                                                <div className="flex justify-between items-center mb-2">
                                                    <Label className={CAMPANHAS_DESIGN.dialog.label}>Mensagem Alternativa (Opcional)</Label>
                                                    <PopoverVariaveis />
                                                </div>
                                                <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                                                    Esta mensagem substitui o template quando a conversa está dentro da janela de 24h.
                                                    <br className="mb-2" />
                                                    <span className="text-green-600 font-medium">Informação:</span> Se você não digitar nada, o template será enviado para todos os contatos.
                                                </p>

                                                <Textarea
                                                    value={body}
                                                    onChange={e => setBody(e.target.value)}
                                                    placeholder="Deixe em branco para enviar sempre o template, ou digite uma mensagem personalizada para janelas abertas..."
                                                    className="resize-none text-base p-4 flex-1 min-h-[200px] border-gray-200 focus:border-[#7C3AED]"
                                                />
                                            </div>
                                        </div>

                                    </div>
                                ) : (
                                    /* Original Layout for Non-WABA or Files */
                                    <>
                                        {isWhatsAppBusiness && (
                                            /* This implies isFile is TRUE because of the logic above covers !isFile */
                                            <div className="mb-4 p-4 bg-green-50 border border-green-100 rounded-lg">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex flex-col">
                                                        <Label className="text-green-900 font-medium">Usar Template (WhatsApp)</Label>
                                                        <span className="text-xs text-green-700">Utilizar um modelo aprovado pelo WhatsApp?</span>
                                                    </div>
                                                    <Switch checked={useTemplate} onCheckedChange={setUseTemplate} disabled={isWhatsAppBusiness} />
                                                </div>

                                                <div className="mb-4 p-2 bg-green-100/50 rounded border border-green-200 text-xs text-green-800 flex items-start gap-2">
                                                    <Iconify icon="mdi:information-outline" className="mt-0.5 shrink-0" width={16} />
                                                    <span>
                                                        Se o contato não estiver dentro da janela de 24 horas, o template será enviado.
                                                    </span>
                                                </div>

                                                {useTemplate && (
                                                    <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                                                        <div>
                                                            <Label className={CAMPANHAS_DESIGN.dialog.label}>Selecione o Template</Label>
                                                            <Select
                                                                value={templateSelecionado?.templateId || templateSelecionado?._id || ''}
                                                                onValueChange={handleSelecionarTemplate}
                                                                disabled={loadingTemplates}
                                                            >
                                                                <SelectTrigger className="bg-white">
                                                                    <SelectValue placeholder={loadingTemplates ? "Carregando templates..." : "Selecione um modelo"} />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {templates.map((t) => (
                                                                        <SelectItem key={t.templateId || t._id} value={t.templateId || t._id}>
                                                                            {t.name || t.templateName} - {t.status}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        </div>

                                                        {templateSelecionado && (
                                                            <div className="space-y-4">
                                                                <div className="p-3 bg-white rounded border border-gray-200 text-sm whitespace-pre-wrap">
                                                                    <span className="font-semibold text-xs text-gray-500 block mb-1">Pré-visualização:</span>
                                                                    {templateSelecionado.conteudo}
                                                                </div>

                                                                {Object.keys(templateVariaveis).length > 0 && (
                                                                    <div className="grid grid-cols-1 gap-3">
                                                                        <Label className="text-xs font-semibold uppercase text-gray-500">Variáveis</Label>
                                                                        {Object.keys(templateVariaveis).map((key) => (
                                                                            <div key={key}>
                                                                                <Label className="text-xs text-gray-600 mb-1">Var {key} {'{{' + key + '}}'}</Label>
                                                                                <Input
                                                                                    value={templateVariaveis[key]}
                                                                                    onChange={(e) => handleAtualizarVariavelTemplate(key, e.target.value)}
                                                                                    placeholder={`Valor para {{${key}}}`}
                                                                                    className="bg-white"
                                                                                />
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {!isWhatsAppBusiness && (
                                            <div className="mb-4 p-4 bg-orange-50 border border-orange-100 rounded-lg flex items-start gap-3">
                                                <div className="p-2 bg-white rounded-full text-orange-600 shadow-sm">
                                                    <Iconify icon="mdi:alert" width={20} />
                                                </div>
                                                <div>
                                                    <h4 className="text-sm font-semibold text-orange-900 mb-1">Atenção: Risco de Bloqueio</h4>
                                                    <p className="text-xs text-orange-700 leading-relaxed">
                                                        O envio de campanhas em massa por canais não oficiais ou pessoais (WhatsApp padrão) possui <strong>alto risco de suspensão ou banimento</strong> do número. Utilize com cautela e evite envios excessivos.
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        <div className="space-y-4">
                                            {!isFile && !isWhatsAppBusiness && (
                                                <div className="animate-in fade-in slide-in-from-top-4 duration-300">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <Label className={CAMPANHAS_DESIGN.dialog.label}>Mensagem de Texto</Label>
                                                        <PopoverVariaveis />
                                                    </div>
                                                    <Textarea
                                                        rows={6}
                                                        value={body}
                                                        onChange={e => setBody(e.target.value)}
                                                        placeholder="Digite aqui a mensagem da sua campanha..."
                                                        className="resize-none text-base p-4"
                                                    />
                                                </div>
                                            )}

                                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 transition-all duration-300">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex flex-col">
                                                        <Label className="text-gray-900 font-medium">Enviar Mídia</Label>
                                                        <span className="text-xs text-gray-500">Enviar arquivo ou áudio em vez de texto?</span>
                                                    </div>
                                                    <Switch checked={isFile} onCheckedChange={setIsFile} />
                                                </div>

                                                {isFile && (
                                                    <div className="animate-in fade-in">
                                                        <div className="grid grid-cols-2 gap-4 mb-6">
                                                            <div
                                                                onClick={() => setTipoArquivo('arquivo')}
                                                                className={cn(
                                                                    "cursor-pointer rounded-xl border-2 p-4 flex flex-col items-center gap-3 transition-all duration-200",
                                                                    tipoArquivo === 'arquivo'
                                                                        ? "border-[#7C3AED] bg-violet-50 text-[#7C3AED]"
                                                                        : "border-gray-200 hover:border-violet-200 hover:bg-gray-50 text-gray-500"
                                                                )}
                                                            >
                                                                <div className={cn("p-2 rounded-full", tipoArquivo === 'arquivo' ? "bg-white" : "bg-gray-100")}>
                                                                    <Iconify icon="mdi:file-document-outline" width={24} />
                                                                </div>
                                                                <span className="text-sm font-semibold">Enviar Arquivo</span>
                                                            </div>

                                                            <div
                                                                onClick={() => setTipoArquivo('audio')}
                                                                className={cn(
                                                                    "cursor-pointer rounded-xl border-2 p-4 flex flex-col items-center gap-3 transition-all duration-200",
                                                                    tipoArquivo === 'audio'
                                                                        ? "border-[#7C3AED] bg-violet-50 text-[#7C3AED]"
                                                                        : "border-gray-200 hover:border-violet-200 hover:bg-gray-50 text-gray-500"
                                                                )}
                                                            >
                                                                <div className={cn("p-2 rounded-full", tipoArquivo === 'audio' ? "bg-white" : "bg-gray-100")}>
                                                                    <Iconify icon="mdi:microphone-outline" width={24} />
                                                                </div>
                                                                <span className="text-sm font-semibold">Gravar Áudio</span>
                                                            </div>
                                                        </div>

                                                        {tipoArquivo === 'arquivo' && (
                                                            <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:bg-gray-100 transition-colors">
                                                                <Input type="file" multiple onChange={handleFileChange} className="hidden" id="file-upload" />
                                                                <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center gap-2">
                                                                    <Iconify icon="mdi:cloud-upload-outline" width={32} className="text-gray-400" />
                                                                    <span className="text-sm font-medium text-gray-600">Clique para selecionar arquivos</span>
                                                                    {files.length > 0 && <span className="text-xs text-[#7C3AED]">{files.length} arquivos selecionados</span>}
                                                                </label>
                                                            </div>
                                                        )}

                                                        {tipoArquivo === 'audio' && (
                                                            <div className="flex items-center justify-center p-4 bg-white rounded-lg border border-gray-200">
                                                                {!audioGravado ? (
                                                                    !recording ? (
                                                                        <Button variant="outline" onClick={handleStartRecording} disabled={loadingAudio} className="w-full">
                                                                            <BsMicFill className="mr-2 text-red-500" /> Iniciar Gravação
                                                                        </Button>
                                                                    ) : (
                                                                        <Button variant="destructive" onClick={handleStopRecording} className="w-full animate-pulse">
                                                                            <div className="w-3 h-3 bg-white rounded-sm mr-2" /> Parar Gravação
                                                                        </Button>
                                                                    )
                                                                ) : (
                                                                    <div className="flex items-center justify-between w-full">
                                                                        <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
                                                                            <FaCheck /> Áudio pronto para envio
                                                                        </div>
                                                                        <Button variant="ghost" size="sm" onClick={() => setAudioGravado(null)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                                                            <Trash2 size={16} className="mr-2" /> Descartar
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* STEP 4: Agendamento */}
                        {activeStep === 4 && (
                            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                                <h3 className={CAMPANHAS_DESIGN.dialog.sectionTitle}>Definir Horário de Envio</h3>
                                <p className={CAMPANHAS_DESIGN.dialog.sectionSubtitle}>Quando você deseja que esta campanha seja disparada?</p>

                                <div className="bg-indigo-50/50 p-6 rounded-xl border border-indigo-100 flex flex-col gap-6">
                                    <div>
                                        <Label className={CAMPANHAS_DESIGN.dialog.label}>Data do Envio</Label>
                                        <Input
                                            type="date"
                                            value={dataCampanha}
                                            onChange={e => setDataCampanha(e.target.value)}
                                            className="bg-white"
                                            disabled={isEdit}
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label className={CAMPANHAS_DESIGN.dialog.label}>Hora</Label>
                                            <Select value={horaCampanha} onValueChange={setHoraCampanha} disabled={isEdit}>
                                                <SelectTrigger className="bg-white">
                                                    <SelectValue placeholder="00" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {Array.from({ length: 19 - 8 }, (_, i) => i + 8).map(h => {
                                                        const val = h.toString().padStart(2, '0');
                                                        const now = new Date();
                                                        const localToday = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
                                                        const isToday = dataCampanha === localToday;
                                                        // Disable if hour is past, or if it's current hour but minutes are almost done (optional, but requested logic "passed time")
                                                        const isDisabled = isToday && (h < now.getHours() || (h === now.getHours() && now.getMinutes() >= 50));
                                                        return <SelectItem key={val} value={val} disabled={isDisabled}>{val}h</SelectItem>
                                                    })}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className={CAMPANHAS_DESIGN.dialog.label}>Minutos</Label>
                                            <Select value={minutoCampanha} onValueChange={setMinutoCampanha} disabled={isEdit}>
                                                <SelectTrigger className="bg-white">
                                                    <SelectValue placeholder="00" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {['00', '10', '20', '30', '40', '50'].map(m => {
                                                        const now = new Date();
                                                        const localToday = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
                                                        const isToday = dataCampanha === localToday;
                                                        const isDisabled = isToday && parseInt(horaCampanha) === now.getHours() && parseInt(m) <= now.getMinutes();
                                                        return <SelectItem key={m} value={m} disabled={isDisabled}>{m}m</SelectItem>
                                                    })}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-4 bg-yellow-50 border border-yellow-100 rounded-lg">
                                    <div className="flex gap-2 text-yellow-800">
                                        <Iconify icon="mdi:information-outline" className="mt-0.5" />
                                        <div className="text-sm">
                                            <span className="font-semibold">Resumo:</span>
                                            <br />
                                            Sua campanha será enviada para <strong>{estimativa || 0} contatos</strong> ({tipoCampanha === 'funil' ? 'Funil' : 'Tags'}) no dia <strong>{fDateTimeSuffix(dataCampanha)}</strong> às <strong>{horaCampanha}:{minutoCampanha}</strong>.
                                        </div>
                                    </div>
                                </div>

                                <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                                    <div className="flex gap-2 text-blue-800">
                                        <Iconify icon="mdi:clock-time-eight-outline" className="mt-0.5" />
                                        <div className="text-sm">
                                            <span className="font-semibold">Horário de Envio:</span>
                                            <br />
                                            Os envios serão realizados das <strong>08:00 às 19:00</strong> para garantir estar dentro das políticas do WhatsApp.
                                            <br />
                                            Caso o envio ultrapasse esse horário, ele será <strong>agendado para o próximo dia</strong>.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter className={CAMPANHAS_DESIGN.dialog.footer}>
                        {activeStep > 1 && (
                            <Button variant="outline" onClick={handleBack} className="gap-2">
                                <ChevronLeft size={16} /> Voltar
                            </Button>
                        )}
                        <div className="flex-1" /> {/* Spacer */}
                        {activeStep < STEPS.length ? (
                            <Button onClick={handleNext} className="gap-2 bg-[#7C3AED] hover:bg-[#6d28d9]">
                                Próximo <ChevronRight size={16} />
                            </Button>
                        ) : (
                            <Button onClick={onSubmit} disabled={loadingEnviarArquivos} className="gap-2 bg-green-600 hover:bg-green-700">
                                <Save size={16} />
                                {loadingEnviarArquivos ? 'Salvando...' : (isEdit ? 'Salvar Alterações' : 'Agendar Campanha')}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog Delete - Kept as is */}
            <Dialog open={modalConfirmarExclusao} onOpenChange={setModalConfirmarExclusao}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirmar Exclusão</DialogTitle>
                        <DialogDescription>
                            Deseja realmente excluir esta campanha? Esta ação não pode ser desfeita.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setModalConfirmarExclusao(false)}>Cancelar</Button>
                        <Button variant="destructive" onClick={onDelete}>Excluir</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Jobs Progress Modal */}
            <Dialog open={jobsModalOpen} onOpenChange={setJobsModalOpen}>
                <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Progresso da Campanha</DialogTitle>
                        <DialogDescription>Acompanhe o status de envio de cada mensagem.</DialogDescription>
                    </DialogHeader>

                    {loadingJobs ? (
                        <div className="flex items-center justify-center p-10">
                            <span className="loading loading-spinner text-[#7C3AED]">Carregando...</span>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-6 overflow-hidden flex-1">
                            {/* Summary Cards */}
                            <div className="grid grid-cols-4 gap-4">
                                <div className="p-4 bg-gray-50 rounded-lg border text-center">
                                    <span className="text-2xl font-bold text-gray-700">{jobsData.summary.total || 0}</span>
                                    <p className="text-xs text-gray-500 uppercase tracking-wider">Total</p>
                                </div>
                                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 text-center">
                                    <span className="text-2xl font-bold text-blue-600">{jobsData.summary.enviado || 0}</span>
                                    <p className="text-xs text-blue-600/80 uppercase tracking-wider">Enviados</p>
                                </div>
                                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100 text-center">
                                    <span className="text-2xl font-bold text-yellow-600">{jobsData.summary.agendado || 0}</span>
                                    <p className="text-xs text-yellow-600/80 uppercase tracking-wider">Agendados</p>
                                </div>
                                <div className="p-4 bg-red-50 rounded-lg border border-red-100 text-center">
                                    <span className="text-2xl font-bold text-red-600">{jobsData.summary.erro || 0}</span>
                                    <p className="text-xs text-red-600/80 uppercase tracking-wider">Erros</p>
                                </div>
                            </div>

                            {/* List */}
                            <div className="flex-1 overflow-auto border rounded-md">
                                <Table>
                                    <TableHeader className="bg-gray-50 sticky top-0">
                                        <TableRow>
                                            <TableHead>Cliente</TableHead>
                                            <TableHead>Telefone</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Data Prevista</TableHead>
                                            <TableHead>Erro</TableHead>
                                            <TableHead></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {jobsData.jobs && jobsData.jobs.length > 0 ? (
                                            jobsData.jobs.map((job) => (
                                                <TableRow key={job._id}>
                                                    <TableCell className="font-medium">{job.nome_cliente}</TableCell>
                                                    <TableCell>{job.numero_cliente}</TableCell>
                                                    <TableCell>
                                                        <Badge variant={
                                                            job.status === 'enviado' ? 'default' :
                                                                job.status === 'erro' ? 'destructive' :
                                                                    'secondary'
                                                        } className={cn("uppercase text-[10px]", job.status === 'enviado' && "bg-green-600 hover:bg-green-700")}>
                                                            {job.status}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>{fDateTimeSuffix(job.data_disparo)}</TableCell>
                                                    <TableCell className="text-red-500 text-xs max-w-[200px] truncate" title={job.erro}>
                                                        {job.erro || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {/* <Button variant="ghost" size="icon" onClick={() => handleDeleteJob(job._id)} className="h-8 w-8 text-gray-400 hover:text-red-500">
                                                            <Trash2 size={14} />
                                                        </Button> */}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={5} className="text-center py-8 text-gray-400">
                                                    Nenhum job encontrado.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button onClick={() => setJobsModalOpen(false)}>Fechar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

        </div >
    );
}
